import { storeToRefs } from 'pinia'
import { type MaybeRefOrGetter, nextTick, toValue, watch } from 'vue'

import { restoreChatInputFocus } from '@/lib/ai/restoreChatInputFocus'
import { useAiAssistantStore } from '@/stores/aiAssistant'

function resolveScrollTarget(fieldKey: string): HTMLElement | null {
  const fieldEl = document.querySelector(`[data-ai-field="${fieldKey}"]`)
  if (!(fieldEl instanceof HTMLElement)) return null

  // Scroll a non-input container so the browser does not focus the form control.
  return (
    fieldEl.closest('[data-ai-scroll-target]') ??
    fieldEl.closest('.v-col') ??
    fieldEl.closest('.mb-4') ??
    fieldEl.parentElement ??
    fieldEl
  )
}

export function useOnboardingFieldChrome(options: { formHidden: MaybeRefOrGetter<boolean> }) {
  const aiStore = useAiAssistantStore()
  const { requestedFieldFocus, onboardingEmbeddedChatActive } = storeToRefs(aiStore)

  const fieldClass = (key: string): string =>
    [
      aiStore.isFieldAiHighlighted(key) ? 'onboarding-field--ai-updated' : '',
      aiStore.isFieldAiSuggested(key) ? 'onboarding-field--ai-focus' : '',
    ]
      .filter(Boolean)
      .join(' ')

  const onFieldFocus = (key: string): void => {
    aiStore.dismissFieldAiHighlight(key)
    aiStore.dismissFieldAiSuggestion(key)
  }

  const scrollToFieldByKey = async (key: string) => {
    // When the form is hidden (chat-only mode) there is nothing to scroll to.
    if (toValue(options.formHidden)) return

    const chatHadFocus = document.activeElement?.closest('.ai-chat-body') != null

    await nextTick()
    const scrollTarget = resolveScrollTarget(key)
    if (!scrollTarget) return

    scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })

    const keepChatFocus = chatHadFocus || onboardingEmbeddedChatActive.value
    if (!keepChatFocus) return

    // Smooth scroll can focus the target control mid-animation; restore chat after it settles.
    window.setTimeout(() => {
      const active = document.activeElement
      if (active instanceof HTMLElement && active.closest('.onboarding-data-entry')) {
        active.blur()
      }
      restoreChatInputFocus()
    }, 400)
  }

  watch(
    requestedFieldFocus,
    async (key) => {
      if (!key) return
      await scrollToFieldByKey(key)
      aiStore.clearRequestedFieldFocus()
    },
    { flush: 'post' }
  )

  return { aiStore, fieldClass, onFieldFocus }
}
